/**
 * Lever, complexity factor and tier configuration.
 *
 * Wording is copied from the reference tool (`reference/salesfix-value-map-builder.html`) and must
 * not drift. In production the server reads lever definitions from the `Value_Map_Lever__mdt` custom
 * metadata type and falls back to these defaults (brief section 7.2, decision D3).
 */

export const LEVER_KEYS = ["fund", "eff", "exp", "risk"] as const;
export type LeverKey = (typeof LEVER_KEYS)[number];

export interface LeverDef {
  key: LeverKey;
  title: string;
  help: string;
  measureHint: string;
}

export const DEFAULT_LEVER_SET = "for-purpose";

export const FOR_PURPOSE_LEVERS: readonly LeverDef[] = [
  {
    key: "fund",
    title: "Fundraising & mission revenue",
    help: "More raised or more funding secured: response, conversion, average gift, retention, grants.",
    measureHint: "e.g. $ uplift in average gift, donor retention %, grants secured",
  },
  {
    key: "eff",
    title: "Team efficiency & capacity",
    help: "Hours saved, less multi-hatting, doing more without more headcount.",
    measureHint: "e.g. hours saved per reporting cycle, roles freed from admin",
  },
  {
    key: "exp",
    title: "Client & supporter experience",
    help: "Better outcomes for the people served, a stronger donor and volunteer experience, reputation.",
    measureHint: "e.g. response time, satisfaction, better client outcomes",
  },
  {
    key: "risk",
    title: "Risk & cost removed",
    help: "Compliance, data and PII security, platform risk, wasted spend.",
    measureHint: "e.g. compliance gaps closed, PII reduced, wasted spend avoided",
  },
];

export const LEVER_SETS: Readonly<Record<string, readonly LeverDef[]>> = {
  [DEFAULT_LEVER_SET]: FOR_PURPOSE_LEVERS,
};

export const COMPLEXITY_FACTOR_KEYS = ["scale", "skills", "dependencies", "change"] as const;
export type ComplexityFactorKey = (typeof COMPLEXITY_FACTOR_KEYS)[number];

export interface ComplexityFactorDef {
  key: ComplexityFactorKey;
  label: string;
  help: string;
}

export const COMPLEXITY_FACTORS: readonly ComplexityFactorDef[] = [
  {
    key: "scale",
    label: "Scale of effort",
    help: "The size of the lift: build and data, or the volume of new business to win and deliver",
  },
  {
    key: "skills",
    label: "Skills and capacity",
    help: "Do we have the people and expertise, or must we hire, train or partner",
  },
  {
    key: "dependencies",
    label: "Dependencies and control",
    help: "Prerequisites and integrations, plus things outside our control like client budgets or the economy",
  },
  {
    key: "change",
    label: "Change and adoption",
    help: "Behaviour change to make it stick, internally and with clients",
  },
];

/** Options shown in every complexity select, value then label. */
export const COMPLEXITY_OPTIONS: readonly (readonly [number, string])[] = [
  [1, "1 · Low"],
  [2, "2"],
  [3, "3 · Medium"],
  [4, "4"],
  [5, "5 · High"],
];

export type TierKey = "quick" | "bet" | "fill" | "recon";

export interface Tier {
  key: TierKey;
  label: string;
}

/** Tier colours from the reference `tierColour` (hex without the leading #). */
export const TIER_COLOURS: Readonly<Record<TierKey, string>> = {
  quick: "57A068",
  bet: "0C86BC",
  recon: "C6791F",
  fill: "6B818B",
};

export const HORIZON_OPTIONS = [1, 2, 3, 4, 5] as const;
export const DEFAULT_HORIZON = 3;
